import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { STATUS_LABELS, SOURCE_COLOR, SOURCE_LABELS, STATUS_COLOR, formatDate, formatDateShort } from '../lib/api';
import { ReservationDialog, NewReservationDialog } from '../components/ReservationDialogs';
import DateStepper from '../components/DateStepper';
import { Search, Plus, Filter, Users, Phone, MessageSquare, Lock, FileText } from 'lucide-react';

const STATUS_FILTERS = ['', 'pending', 'confirmed', 'seated', 'completed', 'cancelled', 'declined', 'no_show'];

function eur(v) {
  return Number(v || 0).toFixed(2).replace('.', ',') + ' €';
}
function qtyStr(q) {
  const n = Number(q) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
}

// Zeigt die bestellten Positionen eines ANGENOMMENEN Angebots direkt unter der
// Reservierung an (immer sichtbar, keine Interaktion). Rendert nichts, wenn das
// Angebot nicht angenommen ist oder keine Positionen hat.
function AcceptedOrderSummary({ offer, resId }) {
  const items = offer?.items || [];
  if (!offer || offer.status !== 'accepted' || items.length === 0) return null;
  return (
    <div
      className="mt-1.5 rounded border border-[#16A34A]/25 bg-[#16A34A]/5 px-2 py-1.5"
      data-testid={`res-order-summary-${resId}`}
    >
      <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#16A34A] mb-1">
        <FileText className="w-3 h-3" /> Bestellung (Angebot angenommen)
      </div>
      <ul className="space-y-0.5">
        {items.map((it, i) => {
          const lt = it.line_total != null ? it.line_total : (Number(it.quantity || 0) * Number(it.unit_price || 0));
          return (
            <li key={i} className="flex items-baseline justify-between gap-3 text-[11px] text-[#1C1C19]">
              <span className="truncate"><span className="font-medium">{qtyStr(it.quantity)}×</span> {it.description}</span>
              <span className="font-mono text-[#5C5C59] shrink-0">{eur(lt)}</span>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between gap-3 mt-1 pt-1 border-t border-[#16A34A]/20 text-[11px] font-semibold text-[#1C1C19]">
        <span>Gesamt</span>
        <span className="font-mono">{eur(offer.total)}</span>
      </div>
      {offer.note && (
        <div className="text-[10px] text-[#5C5C59] mt-1 whitespace-pre-line">{offer.note}</div>
      )}
    </div>
  );
}

export default function AdminReservations() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [filters, setFilters] = useState({
    date: '',
    date_from: '',
    date_to: '',
    status: '',
    area_id: '',
    tag_id: '',
    search: '',
  });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;
  useEffect(() => { setPage(0); }, [filters]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const [r, t, a, tg, cb] = await Promise.all([
        api.get(`/reservations?${params.toString()}`),
        api.get('/tables'),
        api.get('/areas'),
        api.get('/tags'),
        api.get('/table-combinations/all'),
      ]);
      setList(r.data);
      setTables(t.data);
      setAreas(a.data);
      setAllTags(tg.data);
      setCombinations(cb.data || []);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so new customer reservations appear without manual reload
  useEffect(() => {
    const t = setInterval(() => { load(); }, 30000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const tagById = (id) => allTags.find(t => t.id === id);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const paged = list.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="p-4 md:p-8 max-w-7xl" data-testid="admin-reservations-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Buchungen</p>
          <h1 className="font-display text-3xl text-[#1C1C19]">Reservierungen</h1>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="res-add-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm flex items-center gap-2 transition">
          <Plus className="w-4 h-4" /> Neue Reservierung
        </button>
      </header>

      {loadError && (
        <div className="bg-[#FEF2F2] border border-[#DC2626] rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-3" data-testid="res-load-error">
          <span className="text-sm text-[#991B1B]">Die Reservierungen konnten nicht geladen werden. Bitte erneut versuchen.</span>
          <button onClick={load} className="shrink-0 text-sm px-3 py-1.5 rounded border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition">
            Erneut laden
          </button>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-7 gap-3">
        <div className="col-span-2">
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Suche</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#5C5C59]" />
            <input
              type="text" placeholder="Name oder Telefon..."
              value={filters.search}
              onChange={e => setFilters({...filters, search: e.target.value})}
              data-testid="res-search-input"
              className="w-full pl-8 pr-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Von</label>
          <DateStepper
            value={filters.date_from}
            onChange={v => setFilters({...filters, date_from: v, date: ''})}
            testIdPrefix="res-from"
            size="sm"
            inputClassName="w-full"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Bis</label>
          <DateStepper
            value={filters.date_to}
            onChange={v => setFilters({...filters, date_to: v, date: ''})}
            testIdPrefix="res-to"
            size="sm"
            inputClassName="w-full"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Status</label>
          <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} data-testid="res-status-filter" className="w-full px-2 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
            <option value="">Alle</option>
            {STATUS_FILTERS.filter(Boolean).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Bereich</label>
          <select value={filters.area_id} onChange={e => setFilters({...filters, area_id: e.target.value})} data-testid="res-area-filter" className="w-full px-2 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
            <option value="">Alle</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Tag</label>
          <select value={filters.tag_id} onChange={e => setFilters({...filters, tag_id: e.target.value})} data-testid="res-tag-filter" className="w-full px-2 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
            <option value="">Alle</option>
            {allTags.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.icon} {t.name}</option>)}
          </select>
        </div>
      </div>

      {/* Mobile: compact card list */}
      <div className="md:hidden bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]" data-testid="res-mobile-list">
        {list.length === 0 && (
          <div className="px-4 py-10 text-center text-[#5C5C59] text-sm" data-testid="res-empty-mobile">Keine Reservierungen.</div>
        )}
        {paged.map(r => {
          const table = tables.find(t => t.id === r.assigned_table_id);
          const combo = r.assigned_combination_id ? combinations.find(c => c.id === r.assigned_combination_id) : null;
          const tischDisplay = combo
            ? ((combo.table_ids || []).map(tid => tables.find(t => t.id === tid)?.table_number).filter(Boolean).join('+') || combo.name)
            : (table?.table_number || '–');
          const area = areas.find(a => a.id === r.area_id);
          const rowTags = (r.tag_ids || []).map(tagById).filter(Boolean);
          const hasVip = rowTags.some(t => t.is_vip);
          return (
            <button
              key={r.id}
              onClick={() => setSelected(r)}
              data-testid={`res-row-mobile-${r.id}`}
              className={`w-full text-left px-3 py-2.5 active:bg-[#F9F9F7] transition ${hasVip ? 'bg-[#A855F708]' : ''}`}
            >
              <div className="flex items-center gap-2">
                {/* Status colored bar */}
                <span
                  className="w-1 self-stretch rounded-full"
                  style={{ background: STATUS_COLOR[r.status] }}
                ></span>
                {/* Time block */}
                <div className="font-mono text-base leading-tight w-14 shrink-0">
                  <div>{r.start_time}</div>
                  <div className="text-[10px] text-[#5C5C59]">{r.end_time}</div>
                </div>
                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm text-[#1C1C19] truncate">{r.customer_name}</span>
                    {hasVip && <span className="text-[9px] px-1 py-0.5 rounded font-mono uppercase" style={{ background: '#A855F720', color: '#A855F7' }}>VIP</span>}
                    {r.rebooked_at && <span title="Umbuchung" className="text-xs">☔</span>}
                    {r.rebooking_needed && <span className="text-[9px] px-1 py-0.5 rounded font-mono uppercase bg-[#EF4444]/15 text-[#EF4444]">⚠</span>}
                    {r.offer && ['sent', 'accepted'].includes(r.offer.status) && (
                      <span title={r.offer.status === 'accepted' ? 'Angebot angenommen' : 'Angebot gesendet'} data-testid={`res-offer-badge-${r.id}`} className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded font-mono uppercase bg-[#16A34A]/15 text-[#16A34A]">
                        <FileText className="w-2.5 h-2.5" /> Angebot
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-[#5C5C59] mt-0.5 truncate">
                    <span>{formatDateShort(r.date)}</span>
                    <span className="inline-flex items-center gap-0.5">
                      <Users className="w-3 h-3" />{r.persons}
                    </span>
                    {(combo || table) && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          const tid = combo ? (combo.table_ids || [])[0] : table?.id;
                          if (tid) navigate(`/admin/floor-plan?date=${r.date}&highlight=${tid}`);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            const tid = combo ? (combo.table_ids || [])[0] : table?.id;
                            if (tid) navigate(`/admin/floor-plan?date=${r.date}&highlight=${tid}`);
                          }
                        }}
                        title="Im Tischplan anzeigen"
                        data-testid={`res-tisch-link-${r.id}`}
                        className="inline-flex items-center gap-0.5 hover:text-[#1C1C19] active:scale-95 underline-offset-2 hover:underline cursor-pointer"
                      >
                        {combo && <span className="text-[8px] px-1 rounded font-mono uppercase bg-[#3B82F6]/15 text-[#3B82F6]">Gr</span>}
                        T{tischDisplay}
                      </span>
                    )}
                    {area && <span className="truncate">· {area.name}</span>}
                  </div>
                  {rowTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {rowTags.slice(0, 3).map(t => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] leading-tight"
                          style={{ background: (t.color || '#5C5C59') + '20', color: t.color || '#5C5C59' }}
                        >
                          {t.icon && <span>{t.icon}</span>}
                          <span>{t.name}</span>
                        </span>
                      ))}
                      {rowTags.length > 3 && <span className="text-[9px] text-[#5C5C59]">+{rowTags.length - 3}</span>}
                    </div>
                  )}
                  {r.note && (
                    <div className="flex items-center gap-1 text-[11px] text-[#5C5C59] mt-0.5" title={r.note} data-testid={`res-note-mobile-${r.id}`}>
                      <MessageSquare className="w-3 h-3 shrink-0" /><span className="truncate">{r.note}</span>
                    </div>
                  )}
                  {r.internal_note && (
                    <div className="flex items-center gap-1 text-[11px] text-[#B45309] mt-0.5" title={r.internal_note} data-testid={`res-internal-note-mobile-${r.id}`}>
                      <Lock className="w-3 h-3 shrink-0" /><span className="truncate">{r.internal_note}</span>
                    </div>
                  )}
                  <AcceptedOrderSummary offer={r.offer} resId={r.id} />
                </div>
                {/* Phone shortcut */}
                {r.phone && (
                  <a
                    href={`tel:${r.phone}`}
                    onClick={e => e.stopPropagation()}
                    className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#F9F9F7] hover:bg-[#E5E5E0]"
                    title={`Anrufen: ${r.phone}`}
                    data-testid={`res-call-${r.id}`}
                  >
                    <Phone className="w-3.5 h-3.5 text-[#5C5C59]" />
                  </a>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Desktop/Tablet: full table */}
      <div className="hidden md:block bg-white border border-[#E5E5E0] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E0] text-xs uppercase tracking-wider text-[#5C5C59]">
              <th className="px-4 py-3 text-left font-medium">Datum</th>
              <th className="px-4 py-3 text-left font-medium">Zeit</th>
              <th className="px-4 py-3 text-left font-medium">Gast</th>
              <th className="px-4 py-3 text-left font-medium">Personen</th>
              <th className="px-4 py-3 text-left font-medium">Tisch</th>
              <th className="px-4 py-3 text-left font-medium">Quelle</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E0]">
            {list.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-12 text-center text-[#5C5C59]" data-testid="res-empty">Keine Reservierungen gefunden.</td></tr>
            )}
            {paged.map(r => {
              const table = tables.find(t => t.id === r.assigned_table_id);
              const combo = r.assigned_combination_id ? combinations.find(c => c.id === r.assigned_combination_id) : null;
              const tischDisplay = combo
                ? ((combo.table_ids || []).map(tid => tables.find(t => t.id === tid)?.table_number).filter(Boolean).join('+') || combo.name)
                : (table?.table_number || '–');
              const rowTags = (r.tag_ids || []).map(tagById).filter(Boolean);
              const hasVip = rowTags.some(t => t.is_vip);
              return (
                <tr key={r.id} onClick={() => setSelected(r)} data-testid={`res-row-${r.id}`} className={`hover:bg-[#F9F9F7] cursor-pointer transition ${hasVip ? 'bg-[#A855F708]' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs">{formatDateShort(r.date)}</td>
                  <td className="px-4 py-3 font-mono">{r.start_time}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5 flex-wrap">
                      {r.customer_name}
                      {hasVip && <span className="text-[10px] px-1 py-0.5 rounded font-mono uppercase" style={{ background: '#A855F720', color: '#A855F7' }}>VIP</span>}
                      {r.rebooked_at && <span title="Wetter-/Bereichs-Umbuchung" data-testid={`res-rebooked-${r.id}`} className="text-sm">☔</span>}
                      {r.rebooking_needed && <span title="Umbuchung erforderlich" data-testid={`res-rebook-needed-${r.id}`} className="text-[10px] px-1.5 py-0.5 rounded font-mono uppercase bg-[#EF4444]/15 text-[#EF4444]">⚠ Umbuchen</span>}
                      {r.offer && ['sent', 'accepted'].includes(r.offer.status) && (
                        <span title={r.offer.status === 'accepted' ? 'Angebot angenommen' : 'Angebot gesendet'} data-testid={`res-offer-badge-tbl-${r.id}`} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded font-mono uppercase bg-[#16A34A]/15 text-[#16A34A]">
                          <FileText className="w-3 h-3" /> Angebot
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#5C5C59]">{r.phone}</div>
                    {rowTags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`res-tags-${r.id}`}>
                        {rowTags.map(t => (
                          <span
                            key={t.id}
                            title={t.name}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                            style={{ background: (t.color || '#5C5C59') + '20', color: t.color || '#5C5C59' }}
                          >
                            {t.icon && <span className="text-xs leading-none">{t.icon}</span>}
                            <span>{t.name}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {r.note && (
                      <div className="flex items-center gap-1 text-xs text-[#5C5C59] mt-1" title={r.note} data-testid={`res-note-${r.id}`}>
                        <MessageSquare className="w-3 h-3 shrink-0" /><span className="truncate max-w-[220px]">{r.note}</span>
                      </div>
                    )}
                    {r.internal_note && (
                      <div className="flex items-center gap-1 text-xs text-[#B45309] mt-1" title={r.internal_note} data-testid={`res-internal-note-${r.id}`}>
                        <Lock className="w-3 h-3 shrink-0" /><span className="truncate max-w-[220px]">{r.internal_note}</span>
                      </div>
                    )}
                    <div className="max-w-[280px]"><AcceptedOrderSummary offer={r.offer} resId={r.id} /></div>
                  </td>
                  <td className="px-4 py-3">{r.persons}</td>
                  <td className="px-4 py-3">
                    {combo ? (
                      <span title={`Tischgruppe „${combo.name}"`} className="inline-flex items-center gap-1">
                        <span className="text-xs px-1.5 py-0.5 rounded font-mono uppercase bg-[#3B82F6]/15 text-[#3B82F6]">Gruppe</span>
                        <span>{tischDisplay}</span>
                      </span>
                    ) : (tischDisplay)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="w-2 h-2 rounded-full" style={{ background: SOURCE_COLOR[r.source] }}></span>
                      {SOURCE_LABELS[r.source]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs uppercase tracking-wider px-2 py-1 rounded font-mono" style={{ background: STATUS_COLOR[r.status] + '22', color: STATUS_COLOR[r.status] }}>
                      {STATUS_LABELS[r.status]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {list.length > 0 && (
        <div className="flex items-center justify-between gap-3 mt-4 text-sm" data-testid="res-pagination">
          <span className="text-[#5C5C59]" data-testid="res-pagination-info">
            {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, list.length)} von {list.length} Buchungen
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={safePage === 0}
                data-testid="res-page-prev"
                className="px-3 py-1.5 rounded border border-[#E5E5E0] text-[#1C1C19] hover:border-[#1C1C19] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Zurück
              </button>
              <span className="text-[#5C5C59] tabular-nums" data-testid="res-page-label">Seite {safePage + 1} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                data-testid="res-page-next"
                className="px-3 py-1.5 rounded border border-[#E5E5E0] text-[#1C1C19] hover:border-[#1C1C19] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Weiter
              </button>
            </div>
          )}
        </div>
      )}

      <ReservationDialog open={!!selected} onClose={() => setSelected(null)} reservation={selected} tables={tables} areas={areas} onUpdate={load} />
      <NewReservationDialog open={showNew} onClose={() => setShowNew(false)} tables={tables} areas={areas} defaultDate={filters.date || filters.date_from || ''} onCreated={load} />
    </div>
  );
}
